/* eslint-disable max-lines */
import React, { useCallback, useRef, memo } from 'react';
import cn from 'clsx';
import { Divider, Loader } from 'fronton-react';
import { ButtonWithGAClickButton } from 'common/components';
import { CardMedia, MediaCard, MediaCardContent } from 'common/components/media-card';
import { SectionSubTitle } from 'common/components/section-subtitle';
import { SwiperWithNavigation, SwiperSlide } from 'common/components/swiper';
import { Typography } from 'common/components/typography';
import { FEProduct } from 'modules/products';
import { formatToRub } from 'utils/format';
import { minimizeProductImage } from 'utils/minimize-image';
import { analyticsUtils } from 'utils/analytics-utils';
import { useIntersection } from 'common/hooks';
import {
  cardBottom,
  centerCard,
  centerCardContent,
  cardContent,
  horizontalMedia,
  horizontalMediaCard,
  loaderWrapper,
  productCount,
  productImage,
  productName,
  productPrice,
  productPriceCurrency,
  productTotalCount,
  productTotalPrice,
  productTotalPriceCurrency,
  reverseMedia,
  solutionSize,
  solutionTitle,
  basketButton,
  swiper,
  swiperSlideStyle,
} from './solution-card-styles';
import { Solution } from './solution-card-types';
import { productsCountEnding } from './solution-card-utils';

type SolutionCardProps = {
  cardName: string;
  loading: boolean;
  productList: FEProduct[];
  reverse?: boolean;
  solution: Solution;
};

/**
 * Вью списка продуктов для выбранного решения
 */
export const SolutionCard: React.FC<SolutionCardProps> = memo(
  ({ cardName, loading, productList, reverse, solution }) => {
    const productRef = useRef<HTMLDivElement[]>([]);

    useIntersection(productRef, el => {
      const position = el.getAttribute('data-position');

      if (!position) {
        return;
      }

      const product = productList[Number(position)];
      analyticsUtils.impressionsEvent('landingSpaceoKub', `${cardName} - ${solution.title}`, {
        productName: product.displayedName,
        productId: product.productId,
        productPrice: product.price,
        productPosition: (+position + 1).toString(),
        productBelong: 'LeroyMerlin',
      });
    });

    /**
     * Обработчик нажатия на кнопку "В корзину"
     */
    const handleAddToCartBtnClick = useCallback(() => {
      productList.forEach(product => {
        analyticsUtils.addToCartEvent('landingSpaceoKub', `${cardName} - ${solution.title}`, {
          productName: product.displayedName,
          productId: product.productId,
          productPrice: product.price,
          productQuantity: solution[product.productId].productCount,
          productBelong: 'LeroyMerlin',
        });
      });
    }, [cardName, solution, productList]);

    /**
     * Обработчик нажатия кнопки "Следующий слайд"
     */
    const handleNextSlideBtnClick = useCallback(() => {
      analyticsUtils.clickButtonEvent(
        'landingSpaceoKub',
        'switch-forward',
        `${cardName} - ${solution.title}`
      );
    }, [cardName, solution.title]);

    /**
     * Обработчик нажатия кнопки "Предыдущий слайд"
     */
    const handlePrevSlideBtnClick = useCallback(() => {
      analyticsUtils.clickButtonEvent(
        'landingSpaceoKub',
        'switch-backward',
        `${cardName} - ${solution.title}`
      );
    }, [cardName, solution.title]);

    return (
      <MediaCard className={cn(horizontalMediaCard, { [centerCard]: loading })}>
        <CardMedia
          alt={`${cardName} - ${solution.title}`}
          className={cn(horizontalMedia, { [reverseMedia]: reverse })}
          src={solution.imgSrc}
          title={`${cardName} - ${solution.title}`}
          loading="lazy"
        />

        <MediaCardContent className={cn(cardContent, { [centerCardContent]: loading })}>
          {loading ? (
            <div className={loaderWrapper}>
              <Loader />
            </div>
          ) : (
            <>
              <SectionSubTitle as="h3" variant="h1" className={solutionTitle} margin="s">
                {solution.title}
              </SectionSubTitle>
              <Typography variant="s" size="body_short" className={solutionSize}>
                {solution.size}
              </Typography>

              <SwiperWithNavigation
                className={swiper}
                onSlideNextTransitionEnd={handleNextSlideBtnClick}
                onSlidePrevTransitionEnd={handlePrevSlideBtnClick}
                slidesPerView="auto"
                staticContainer
                watchOverflow>
                {productList.map((product: FEProduct, index: number) => (
                  <SwiperSlide key={product.productId} className={swiperSlideStyle}>
                    <div
                      data-position={index}
                      ref={(el: HTMLDivElement) => {
                        if (el) productRef.current[index] = el;
                      }}>
                      <img
                        width="115"
                        height="115"
                        src={minimizeProductImage(product.mediaMainPhoto)}
                        className={productImage}
                        title={product.displayedName}
                        alt={product.displayedName}
                        loading="lazy"
                      />
                      <Typography variant="s" size="body_long" className={productCount}>
                        {solution[product.productId].productCount} шт.
                      </Typography>
                      <Typography variant="s" size="body_short" className={productName}>
                        {product.displayedName}
                      </Typography>
                      <Typography variant="l" size="body_accent" className={productPrice}>
                        {formatToRub(Number(product.price))}{' '}
                        <span className={productPriceCurrency}>₽/шт.</span>
                      </Typography>
                    </div>
                  </SwiperSlide>
                ))}
              </SwiperWithNavigation>

              <Divider />

              <div className={cardBottom}>
                <div>
                  <Typography variant="m" size="body_long" className={productTotalCount}>
                    {solution.totalCount} {productsCountEnding(solution.totalCount)} в комплекте
                  </Typography>
                  <Typography variant="h1" className={productTotalPrice}>
                    {formatToRub(solution.totalPrice)} <span className={productTotalPriceCurrency}>₽</span>
                  </Typography>
                </div>
                <ButtonWithGAClickButton
                  className={basketButton}
                  elementBlock="landingSpaceoKub"
                  elementName={`${cardName} - ${solution.title}`}
                  elementText="В корзину"
                  onClick={handleAddToCartBtnClick}
                  href={solution.basketHref}
                  title="Добавить товары в корзину"
                  target="_blank">
                  В корзину
                </ButtonWithGAClickButton>
              </div>
            </>
          )}
        </MediaCardContent>
      </MediaCard>
    );
  }
);
